import {
    fetchAction,
    fetchPostApi,
    fetchActionPost,
    fetchPostFormData,
} from "../../../helpers/fetch";
import { produce } from "immer";
import { fetchApi } from "../../../helpers/fetch";
import { serialize } from "../../../helpers/serialize";
import batchManager from "../../../helpers/batchManager";

/** Fetch admin news list with optional filters (page, limit, search) */
export const fetchAdminNews = (params = {}) => {
    const query = serialize({
        page: params.page ?? 1,
        limit: params.limit ?? 18,
        ...(params.search && { search: params.search }),
        ...(params.sort_by && { sort_by: params.sort_by }),
        ...(params.sort_dir && { sort_dir: params.sort_dir }),
    });
    return fetchApi(`/admin/news?${query}`);
};

/** Fetch admin news detail by id */
export const fetchAdminNewsDetail = (news_id) =>
    fetchApi(`/admin/news/${news_id}`);

export const fetchReportNews = (news_id, reason) =>
    fetchPostApi(`/api/news/${news_id}/add/report`, { news_id, reason });
export const fetchAddNews = (newsData) =>
    fetchPostFormData("/api/news/add", newsData);

export const fetchUpdateNews = (newsId, newsData) =>
    fetchPostFormData(`/api/news/${newsId}`, newsData, 'PUT');

// Fetch all news
export const fetchAllNews = (page, pageSize) =>
    fetchAction(
        `/api/news?page=${page}&pageSize=${pageSize}    `,
        [
            "FETCH_ALL_NEWS_REQUEST",
            "FETCH_ALL_NEWS_SUCCESS",
            "FETCH_ALL_NEWS_FAILURE",
        ],
        true
    );

export const fetchAllNewsPaginate = (page, pageSize) =>
    fetchAction(
        `/api/news?page=${page}&pageSize=${pageSize}    `,
        [
            "FETCH_ALL_NEWS_REQUEST",
            "FETCH_ALL_NEWS_SUCCESS_PAGINATE",
            "FETCH_ALL_NEWS_FAILURE",
        ],
        true
    );

// Fetch news by creator ID
export const fetchNewsByCreator = (creatorId) =>
    fetchAction(
        `/api/news/${creatorId}`,
        ["FETCH_NEWS_REQUEST", "FETCH_NEWS_SUCCESS", "FETCH_NEWS_FAILURE"],
        true
    );

export const fetchNewsComments = (news_id) =>
    fetchAction(`/api/news/${news_id}/comments`, [
        "FETCH_NEWS_COMMENTS_REQUEST",
        "FETCH_NEWS_COMMENTS_SUCCESS",
        "FETCH_NEWS_COMMENTS_FAILURE",
    ]);


// Save/Unsave News API call
export const fetchSaveUnSaveNews = (news_id, unsave = false) =>
    fetchPostApi(`/api/news/${news_id}/add/saved${unsave ? "?delete=true" : ""}`);

export const fetchDeleteNews = (news_id) =>
    fetchPostApi(`/api/news/${news_id}`, {}, 'DELETE')

// Save/Unsave News state update
export const saveUnSaveNews = (
    news_id,
    isSave,
    user_id = null
) => {
    return {
        type: "SET_LIKE_SAVE_NEWS",
        payload: {
            news_id,
            isSave,
            actioned: "save",
            user_id,
        },
    };
};

// Like/Unlike News API calls
export const fetchLikeNews = (news_id) =>
    fetchPostApi(`/api/likes/news_likes/${news_id}/like`);
export const fetchUnlikeNews = (news_id) =>
    fetchPostApi(`/api/likes/news_likes/${news_id}/unlike`);

// True only when the given user's like is in news.is_liked (HYK-1111).
// Never infer "liked" from is_liked.length: that array may hold other users' likes.
export const isNewsLikedByUser = (news, user_id) =>
    Boolean(user_id) &&
    (news?.is_liked || []).some(like =>
        (typeof like === 'object' && like !== null ? like.user_id : like) === user_id
    );

// Like/Unlike News state update
export const likeUnlikeNews = (
    news_id,
    isLike,
    user_id = null
) => {
    return {
        type: "SET_LIKE_SAVE_NEWS",
        payload: {
            news_id,
            isLike,
            actioned: "like",
            user_id,
        },
    };
};

// Add comment to news
export const fetchAddCommentNews = (bodyContent) =>
    fetchActionPost(`/api/news/${bodyContent.news_id}/add/comment`, bodyContent, [
        "ADD_COMMENT_NEWS_REQUEST",
        "ADD_COMMENT_NEWS_SUCCESS",
        "ADD_COMMENT_NEWS_FAILURE",
    ]);

// Fetch saved news by user ID
export const fetchSavedNewsByUser = (userId) =>
    fetchAction(`/api/news/${userId}/saved`, [
        "FETCH_SAVED_NEWS_REQUEST",
        "FETCH_SAVED_NEWS_SUCCESS",
        "FETCH_SAVED_NEWS_FAILURE",
    ]);

// Fetch news by ID
export const fetchNewsById = (newsId) =>
    fetchAction(`/api/news/${newsId}`, [
        "FETCH_NEWS_BY_ID_REQUEST",
        "FETCH_NEWS_BY_ID_SUCCESS",
        "FETCH_NEWS_BY_ID_FAILURE",
    ]);

// News search overlay and mode actions
export const openSearchOverlayNews = () => ({
    type: "OPEN_SEARCH_OVERLAY_NEWS",
});
export const closeSearchOverlayNews = () => ({
    type: "CLOSE_SEARCH_OVERLAY_NEWS",
});
export const setSearchOverlayQueryNews = (query) => ({
    type: "SET_SEARCH_OVERLAY_QUERY_NEWS",
    payload: query,
});
export const enterSearchModeNews = () => ({ type: "ENTER_SEARCH_MODE_NEWS" });
export const exitSearchModeNews = () => ({ type: "EXIT_SEARCH_MODE_NEWS" });

// Search news by query
const fetchSearchNews = (query) =>
    fetchApi(`/api/news/search?query=${encodeURIComponent(query)}`);

export const searchNews = (query) => {
    let isSubscribed = true;

    return async (dispatch) => {
        dispatch({ type: "SEARCH_NEWS_REQUEST" });
        try {
            const response = await fetchSearchNews(query);
            if (isSubscribed) {
                if (response.success) {
                    dispatch({
                        type: "SEARCH_NEWS_SUCCESS",
                        payload: response.data,
                    });
                    return { success: true, data: response.data };
                } else {
                    dispatch({
                        type: "SEARCH_NEWS_FAILURE",
                        payload: response.message || "Search failed",
                    });
                    return { success: false, message: response.message };
                }
            }
        } catch (error) {
            if (isSubscribed) {
                console.error("Error searching news:", error);
                dispatch({
                    type: "SEARCH_NEWS_FAILURE",
                    payload: "Failed to search news",
                });
                return { success: false, message: "Failed to search news" };
            }
        }
        return () => {
            isSubscribed = false;
        };
    };
};

export const fetchDetailNewsById = (news_id) =>
    fetchAction(`/api/news/detail/${news_id}`, [
        "FETCH_DETAIL_NEWS_BY_ID_REQUEST",
        "FETCH_DETAIL_NEWS_BY_ID_SUCCESS",
        "FETCH_DETAIL_NEWS_BY_ID_FAILURE",
    ]);

export const setActiveTabNews = (tab) => {
    return {
        type: "SET_ACTIVE_TAB_NEWS",
        payload: tab,
    };
};

export const setViewNews = (news) => {
    return {
        type: "SET_VIEW_NEWS",
        payload: news,
    };
};

export const setSavedNews = (news) => {
    return {
        type: "SET_SAVED_NEWS",
        payload: news,
    };
};

export const setAllNews = (news) => {
    return {
        type: "SET_ALL_NEWS",
        payload: news,
    };
};

export const filterNews = (state) => {
    const allNews = state.news.allNews;
    const activeTab = state.news.activeTab;
    const user = state.auth.user;

    let filteredNews;
    // TODO: what is that? use BE filtering instead
    if (activeTab === "all" || !activeTab || !user) {
        filteredNews = allNews;
    } else if (activeTab === "subscriptions") {
        filteredNews = allNews?.filter((item) => {
            return (user?.subscriptions || []).some(sub => sub.user_id === item.creator_id)
        });
    } else if (activeTab === "saved") {

        filteredNews = allNews?.filter(item =>
            item?.is_saved?.some(saved => saved.user_id === user.user_id)
          ) ?? [];
    }
    filteredNews = Array.isArray(filteredNews) ? filteredNews : [];
    // Sort by recency (most recent first)
    return filteredNews.sort((a, b) => {
        const dateA = new Date(a.created_at || a.updated_at || 0);
        const dateB = new Date(b.created_at || b.updated_at || 0);
        return dateB - dateA;
    });
};


export const updateReportOnNews = (news_id) => ({
    type: 'UPDATE_REPORTED_NEWS',
    payload: { news_id }
})

export const fetchNewsReports = () =>
    fetchAction('/admin/reported-news', [
        'FETCH_NEWS_REPORTS_REQUEST',
        'FETCH_NEWS_REPORTS_SUCCESS',
        'FETCH_NEWS_REPORTS_FAILURE'
    ], true);

export const fetchManageNewsReport = (news_id, action) =>
    fetchPostApi(`/admin/news/${news_id}/manage-report`, { action });

export const manageNewsReport = (news_id, action) => ({
    type: 'MANAGE_NEWS_REPORT',
    payload: { news_id, action }
});

export const fetchAdminReportNews = (news_id, reason) =>
    fetchPostApi(`/admin/news/${news_id}/add/report`, { news_id, reason });

// Optimistic update actions for batching
export const optimisticLikeNews = (user_id, news_id, action, newsItems, type, searchResults) => {
    const updatedNews = produce(newsItems, (draft) => {
        const news = draft.find((n) => n.id === news_id);
        if (news) {
            if (!Array.isArray(news.likes)) {
                news.likes = [];
            }
            const index = news.likes.indexOf(user_id);
            if (action && index === -1) {
                news.likes.push(user_id);
            } else if (!action && index !== -1) {
                news.likes.splice(index, 1);
            }
        }
    });

    return {
        type: "OPTIMISTIC_LIKE_UPDATE",
        payload: {
            updatedNews,
            updatedSearchResults: searchResults?.map((news) => {
                if (news.id !== news_id) return news;
                const updatedLikes = [...(news.likes || [])];
                const index = updatedLikes.indexOf(user_id);
                if (action && index === -1) {
                    updatedLikes.push(user_id);
                } else if (!action && index !== -1) {
                    updatedLikes.splice(index, 1);
                }
                return {
                    ...news,
                    likes: updatedLikes,
                    liked: action,
                    total_likes: updatedLikes.length
                };
            }) || [],
            type,
            news_id,
            action,
            actioned: "like",
        },
    };
};

export const optimisticSaveNews = (user_id, news_id, action, newsItems, type, searchResults) => {
    const updatedNews = produce(newsItems, (draft) => {
        const news = draft.find((n) => n.id === news_id);
        if (news) {
            if (!Array.isArray(news.saved)) {
                news.saved = [];
            }
            const index = news.saved.indexOf(user_id);
            if (action && index === -1) {
                news.saved.push(user_id);
            } else if (!action && index !== -1) {
                news.saved.splice(index, 1);
            }
        }
    });

    return {
        type: "OPTIMISTIC_SAVE_UPDATE",
        payload: {
            updatedNews,
            updatedSearchResults: searchResults?.map((news) => {
                if (news.id !== news_id) return news;
                return {
                    ...news,
                    saved: action,
                };
            }) || [],
            type,
            news_id,
            action,
            actioned: "save",
        },
    };
};

// Batched like/unlike with optimistic updates
export const batchedLikeUnlikeNews = (user_id, news_id, action, newsItems, type, searchResults) => {
    return (dispatch) => {
        // Dispatch optimistic update immediately
        dispatch(optimisticLikeNews(user_id, news_id, action, newsItems, type, searchResults));

        // Add to batch queue
        batchManager.addOperation(
            'like',
            news_id,
            user_id,
            action,
            () => {
                // Success callback - operation confirmed
                console.log(`Like ${action ? 'added' : 'removed'} successfully for news ${news_id}`);
            },
            (error) => {
                // Error callback - revert optimistic update
                console.error('Failed to process like operation:', error);
                dispatch(revertLikeUpdate(user_id, news_id, !action, newsItems, type, searchResults));
            }
        );
    };
};

// Batched save/unsave with optimistic updates
export const batchedSaveUnSaveNews = (user_id, news_id, action, newsItems, type, searchResults) => {
    return (dispatch) => {
        // Dispatch optimistic update immediately
        dispatch(optimisticSaveNews(user_id, news_id, action, newsItems, type, searchResults));

        // Add to batch queue
        batchManager.addOperation(
            'save',
            news_id,
            user_id,
            action,
            () => {
                // Success callback - operation confirmed
                console.log(`Save ${action ? 'added' : 'removed'} successfully for news ${news_id}`);
            },
            (error) => {
                // Error callback - revert optimistic update
                console.error('Failed to process save operation:', error);
                dispatch(revertSaveUpdate(user_id, news_id, !action, newsItems, type, searchResults));
            }
        );
    };
};

// Revert like update (for error handling)
export const revertLikeUpdate = (user_id, news_id, action, newsItems, type, searchResults) => {
    const revertedNews = produce(newsItems, (draft) => {
        const news = draft.find((n) => n.id === news_id);
        if (news) {
            if (!Array.isArray(news.likes)) {
                news.likes = [];
            }
            const index = news.likes.indexOf(user_id);
            if (action && index === -1) {
                news.likes.push(user_id);
            } else if (!action && index !== -1) {
                news.likes.splice(index, 1);
            }
        }
    });

    return {
        type: "REVERT_LIKE_UPDATE",
        payload: {
            updatedNews: revertedNews,
            updatedSearchResults: searchResults?.map((news) => {
                if (news.id !== news_id) return news;
                const revertedLikes = [...(news.likes || [])];
                const index = revertedLikes.indexOf(user_id);
                if (action && index === -1) {
                    revertedLikes.push(user_id);
                } else if (!action && index !== -1) {
                    revertedLikes.splice(index, 1);
                }
                return {
                    ...news,
                    likes: revertedLikes,
                    liked: action,
                    total_likes: revertedLikes.length
                };
            }) || [],
            type,
            news_id,
            action,
            actioned: "like",
        },
    };
};

// Revert save update (for error handling)
export const revertSaveUpdate = (user_id, news_id, action, newsItems, type, searchResults) => {
    const revertedNews = produce(newsItems, (draft) => {
        const news = draft.find((n) => n.id === news_id);
        if (news) {
            if (!Array.isArray(news.saved)) {
                news.saved = [];
            }
            const index = news.saved.indexOf(user_id);
            if (action && index === -1) {
                news.saved.push(user_id);
            } else if (!action && index !== -1) {
                news.saved.splice(index, 1);
            }
        }
    });

    return {
        type: "REVERT_SAVE_UPDATE",
        payload: {
            updatedNews: revertedNews,
            updatedSearchResults: searchResults?.map((news) => {
                if (news.id !== news_id) return news;
                return {
                    ...news,
                    saved: action,
                };
            }) || [],
            type,
            news_id,
            action,
            actioned: "save",
        },
    };
};